import { NgClass } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { clone, Dictionary, find, isEqual, transform } from "lodash";
import { AccessResult, AccessRight, SystemIndicators } from "rl-common/rl-common.consts";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { ISecurityPolicy, ISecurityPolicyPermission } from "rl-common/services/company/company.models";
import { IFieldPermissionDelta, ITemplateFieldPolicySummaryModel } from "rl-common/services/entity-config/entity-config.models";
import { AclUri, AclUtil } from "rl-common/utils/acl.util";
import { LoaderComponent } from "rl-common/components/panel/loader/loader.component";
import { SecurityPolicyCellComponent } from "./security-policy-cell/security-policy-cell.component";
import { ICreateTemplateFormData } from "./template-field-policy-editor.models";

@Component({
	selector: "rl-template-field-policy-editor",
	templateUrl: "./template-field-policy-editor.component.html",
	styleUrls: ["./template-field-policy-editor.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgClass, RouterLink, SecurityPolicyCellComponent, LoaderComponent]
})
export class TemplateFieldPolicyEditorComponent implements OnChanges {

	contextPermissions: Dictionary<ISecurityPolicyPermission>;
	/** This field's own permission per policy as last saved, which edits are compared against. */
	private savedPermissions: Dictionary<ISecurityPolicyPermission> = {};
	/** The section the last `permissions` were loaded for; the endpoint only returns rows for that section. */
	private permissionsCharGroupId: number;
	rightStatus: Dictionary<Dictionary<number>> = {};
	effectiveClass: Dictionary<Dictionary<string>> = {};
	acls: Dictionary<string[]>;

	statusKey = { allow: AccessResult.Allow, deny: AccessResult.Deny, neutral: AccessResult.NotSet };

	securityRights = [
		{
			name: "Read",
			rightFlag: AccessRight.Read
		},
		{
			name: "Write",
			rightFlag: AccessRight.Write
		},
		{
			name: "Create",
			rightFlag: AccessRight.Create
		},
		{
			name: "Delete",
			rightFlag: AccessRight.Delete
		}
	];

	/** The rights compared when working out what changed: the visible four plus the hidden Admin right. */
	private readonly deltaRights = [AccessRight.Read, AccessRight.Write, AccessRight.Create, AccessRight.Delete, AccessRight.Admin];

	@Input()
	form: FormGroup<ICreateTemplateFormData>;

	@Input()
	policies: ISecurityPolicy[];

	@Input()
	permissions: { [key: string]: ISecurityPolicyPermission[] };

	@Input()
	policiesLoaded: boolean;

	@Input()
	loadError = false;

	@Input()
	isReadOnly = false;

	@Input()
	policyModel: ITemplateFieldPolicySummaryModel;

	@Input()
	systemIndicator: number;

	@Input()
	tagLabel: string;

	@Output()
	contextPermissionsChange = new EventEmitter<Dictionary<ISecurityPolicyPermission>>();

	@Output()
	permissionDeltasChange = new EventEmitter<Dictionary<IFieldPermissionDelta>>();

	constructor() { }

	ngOnChanges(changes: ComponentChanges<this>): void {
		const permissionsChanged = changes.permissions && !isEqual(changes.permissions.previousValue, changes.permissions.currentValue);
		if ((permissionsChanged || changes.policies) && this.permissions && this.policies) {
			this.getAcls();
			if (this.isSectionReload()) {
				this.followSection();
			} else {
				this.setContextFromAcls();
				this.applySystemDefaults();
			}
			this.permissionsCharGroupId = this.policyModel.charGroupId;
			this.updateStatus();
		} else if (changes.systemIndicator && !changes.systemIndicator.firstChange &&
			changes.systemIndicator.previousValue !== SystemIndicators.System && this.contextPermissions && this.isSystemField) {
			this.applySystemDefaults();
			this.updateStatus();
		} else if (changes.policyModel && !changes.policyModel.firstChange && this.contextPermissions) {
			this.followSection();
			this.updateStatus();
		}

		if (changes.contextPermissions && isEqual(changes.contextPermissions.previousValue, changes.contextPermissions.currentValue)) {
			console.log("Context permissions changed");
		}
	}

	getAcls() {
		this.acls = Object.keys(this.permissions).reduce((acc, key) => {
			const aclStrings = this.permissions[key].map(p => this.permissionToAcl(p));
			acc[key] = aclStrings;

			return acc;
		}, {});
	}

	getEffectiveStatusClass(effectiveStatus: number) {
		let output = "effective-neutral";
		switch (effectiveStatus) {
			case 1:
				output = "success";
				break;
			case -1:
				output = "danger";
				break;
		}

		return output;
	}

	setContextFromAcls() {
		this.savedPermissions = transform(this.permissions, (acc, value, key) => acc[key] =
			find(value, p =>
				p.charTypeID === this.policyModel.charTypeId &&
				p.templateGroupID === this.policyModel.templateGroupId &&
				p.templateID === this.policyModel.templateId &&
				p.charGroupID === this.policyModel.charGroupId &&
				p.charID === this.policyModel.charId) ||
			null, {} as Dictionary<ISecurityPolicyPermission>);
		this.contextPermissions = this.cloneSaved();
	}

	private cloneSaved() {
		return transform(this.savedPermissions, (acc, value, key) => acc[key] = clone(value), {} as Dictionary<ISecurityPolicyPermission>);
	}

	/**
	 * The rows were reloaded because the field's section changed, possibly back to the one first loaded. They hold that section's
	 * inherited rows only, so the field's own saved values and unsaved edits are kept rather than looked up again; they move with the
	 * field when it's saved.
	 */
	private isSectionReload() {
		return !!this.contextPermissions && this.permissionsCharGroupId != null;
	}

	/** Keeps the field's own values as loaded, at the section it's moving to, so the shading (and Reset) follows that section. */
	private followSection() {
		const charGroupID = this.policyModel.charGroupId;
		const moveTo = (permissions: Dictionary<ISecurityPolicyPermission>) => transform(permissions ?? {}, (acc, value, key) =>
			acc[key] = value ? { ...value, charGroupID } : value, {} as Dictionary<ISecurityPolicyPermission>);
		this.savedPermissions = moveTo(this.savedPermissions);
		this.contextPermissions = moveTo(this.contextPermissions);
	}

	permissionToAcl(p: ISecurityPolicyPermission) {
		const acl = AclUtil.getDataEntityAcl(p.charTypeID, p.templateGroupID, p.templateID, p.charGroupID, p.charID);
		return AclUri.withPermissionValue(acl, p.permissionValue);
	}

	getCharAcl() {
		const charTypeId = this.policyModel.charTypeId;
		const templateGroupId = this.policyModel.templateGroupId;
		const templateId = this.policyModel.templateId;
		const charGroupId = this.policyModel.charGroupId;
		const charId = this.policyModel.charId;
		return AclUtil.getDataEntityAcl(charTypeId, templateGroupId, templateId, charGroupId, charId);
	}

	toggle(policy: ISecurityPolicyPermission, rightFlag: number) {
		if (this.isReadOnly) {
			return;
		}

		if (!this.contextPermissions[policy.securityPolicyID]) {
			this.contextPermissions[policy.securityPolicyID] = this.createNewPermission(policy.securityPolicyID);
		}

		const permission = this.contextPermissions[policy.securityPolicyID];
		const currentRightStatus = AclUtil.getRightStatus(permission.permissionValue, rightFlag);

		let newStatus: number;

		if (currentRightStatus === AccessResult.NotSet) {
			newStatus = AccessResult.Allow;
		} else if (currentRightStatus === AccessResult.Allow) {
			newStatus = AccessResult.Deny;
		} else if (currentRightStatus === AccessResult.Deny) {
			newStatus = AccessResult.NotSet;
		}

		permission.permissionValue = AclUtil.setRightStatus(permission.permissionValue, rightFlag, newStatus);

		policy.rightStatus = newStatus;

		this.updateStatus();
	}

	// Likely deprecated, keeping just in case
	// setAsHidden() {
	// 	this.contextPermissions = Object.keys(this.contextPermissions).reduce((acc, securityPolicyId) => {
	//         let permission = this.contextPermissions[securityPolicyId];
	//         if (!permission) {
	//             permission = this.createNewPermission(securityPolicyId);
	//             acc[securityPolicyId] = permission;
	//         } else {
	//             permission.permissionValue = AclUtil.setRightStatus(permission.permissionValue, AccessRight.All, -1);
	//         }
	//         return acc;
	//     }, {} as Dictionary<ISecurityPolicyPermission>);
	// }

	/** System fields, except the record title, default to Write/Create/Delete/Admin denied on every policy, as in Classic. */
	get isSystemField() {
		return this.systemIndicator === SystemIndicators.System && this.tagLabel !== "entity_title";
	}

	private applySystemDefaults() {
		if (this.isSystemField && !this.isReadOnly) {
			this.setAsInternal();
		}
	}

	/** Denies Write/Create/Delete/Admin on every policy and leaves Read, keeping each policy's saved entry. */
	setAsInternal() {
		const allButRead = AccessRight.Write | AccessRight.Create | AccessRight.Delete | AccessRight.Admin;
		const policyIds = new Set([...Object.keys(this.contextPermissions ?? {}), ...(this.policies ?? []).map(p => p.securityPolicyID)]);
		this.contextPermissions = Array.from(policyIds).reduce((acc, securityPolicyId) => {
			const permission = this.contextPermissions?.[securityPolicyId] ?? this.createNewPermission(securityPolicyId);
			permission.permissionValue = AclUtil.setRightStatus(permission.permissionValue, allButRead, AccessResult.Deny);
			acc[securityPolicyId] = permission;
			return acc;
		}, {} as Dictionary<ISecurityPolicyPermission>);
	}

	private createNewPermission(securityPolicyId: string) {
		return {
			permissionValue: 0,
			charTypeID: this.policyModel.charTypeId,
			templateGroupID: this.policyModel.templateGroupId,
			templateID: this.policyModel.templateId,
			charGroupID: this.policyModel.charGroupId,
			charID: this.policyModel.charId,
			securityPolicyID: securityPolicyId,
			securityPolicyPermissionID: null
		} as ISecurityPolicyPermission;
	}

	updateStatus() {
		this.rightStatus = {};
		this.effectiveClass = {};
		const charAcl = this.getCharAcl();

		(this.policies ?? []).forEach(p => {
			// reset lookup dictionaries
			this.rightStatus[p.securityPolicyID] = {};
			this.effectiveClass[p.securityPolicyID] = {};
			// get acls including any being edited/created in this context
			const effectiveAcls = this.getEffectiveAcls(p.securityPolicyID);
			// get hierarchical permission value
			const effectivePermissionValue = AclUtil.getEffectivePermissionValue(effectiveAcls, charAcl);
			// get permission for this exact charId
			const contextPermission = this.contextPermissions[p.securityPolicyID];

			this.securityRights.forEach(s => {
				// get status for this charId
				const contextStatus = AclUtil.getRightStatus(contextPermission?.permissionValue ?? 0, s.rightFlag);
				// get status across all acls
				const effectivePermissionStatus = AclUtil.getRightStatus(effectivePermissionValue, s.rightFlag);
				this.rightStatus[p.securityPolicyID][s.rightFlag] = contextStatus;
				this.effectiveClass[p.securityPolicyID][s.rightFlag] = this.getEffectiveStatusClass(effectivePermissionStatus);
			});
		});

		this.contextPermissionsChange.emit(this.contextPermissions);
		this.permissionDeltasChange.emit(this.getPermissionDeltas());
	}

	/**
	 * The rights changed on each policy since the last save. Only those rights are sent, so the server can merge them
	 * onto the saved row (keeping its ID, hidden Admin bits and other admins' changes) instead of replacing it.
	 */
	getPermissionDeltas() {
		return Object.keys(this.contextPermissions ?? {}).reduce((acc, policyId) => {
			const savedValue = this.savedPermissions[policyId]?.permissionValue ?? 0;
			const currentValue = this.contextPermissions[policyId]?.permissionValue ?? 0;
			const changedRightsMask = this.deltaRights
				.filter(right => AclUtil.getRightStatus(savedValue, right) !== AclUtil.getRightStatus(currentValue, right))
				.reduce((mask, right) => mask | right, 0);
			if (changedRightsMask) {
				acc[policyId] = { changedRightsMask, permissionValue: currentValue };
			}
			return acc;
		}, {} as Dictionary<IFieldPermissionDelta>);
	}

	/** The loaded ACLs with this field's own entry replaced by the one being edited, leaving `acls` untouched. */
	getEffectiveAcls(policyId: string) {
		const charAclPath = this.getCharAcl().split("?")[0];
		const inheritedAcls = (this.acls[policyId] ?? []).filter(acl => acl.split("?")[0] !== charAclPath);
		const contextPermission = this.contextPermissions[policyId];

		return contextPermission?.permissionValue ? [...inheritedAcls, this.permissionToAcl(contextPermission)] : inheritedAcls;
	}

	setAll(event: Event, rightFlag: number, status: number) {
		event.preventDefault();

		if (this.isReadOnly) {
			return;
		}

		this.policies.forEach(policy => {
			if (!this.contextPermissions[policy.securityPolicyID]) {
				if (status !== 0) {
					this.contextPermissions[policy.securityPolicyID] = this.createNewPermission(policy.securityPolicyID);
				} else {
					return;
				}
			}

			const permission = this.contextPermissions[policy.securityPolicyID];
			permission.permissionValue = AclUtil.setRightStatus(permission.permissionValue, rightFlag, status);
		});

		this.updateStatus();
	}

	resetPolicyChanges() {
		if (this.isReadOnly) {
			return;
		}

		this.contextPermissions = this.cloneSaved();
		this.applySystemDefaults();
		this.updateStatus();
	}
}